import type { ReactNode } from "react";
import { FileText, ExternalLink, Mic, Megaphone } from "lucide-react";
import type { WhatsAppMedia } from "../api/client";

const schoolDocuments: Record<string, { title: string; file: string }> = {
  school_preescolar: { title: "Preescolar", file: "Ruta_Magica_Preescolar.pdf" },
  school_primaria: { title: "Primaria", file: "Ruta_Magica_Primaria.pdf" },
  school_secundaria: { title: "Secundaria", file: "Ruta_Magica_Secundaria.pdf" },
  school_bachillerato: { title: "Bachillerato", file: "Ruta_Magica_Bachillerato.pdf" },
  school_dossier: { title: "Todos los niveles", file: "ruta-magica-dossier-escolar.pdf" },
};

/** Label of a PDF sent from the panel, from what it was. */
function documentTitle(metadata: Record<string, unknown>) {
  if (metadata.quote_id) return "Cotización";
  if (metadata.brochure) return "Brochure escolar";
  if (metadata.document === "recibo") return "Comprobante de pago";
  if (metadata.document === "confirmacion") return "Confirmación con pagos";
  if (metadata.document === "apartado" || metadata.document === "apartado-recibido") return "Apartado de fecha";
  return "Documento";
}

type MessageBlock =
  | { type: "paragraph"; lines: string[] }
  | { type: "list"; items: string[] };

/** Labels the bridge writes for a file; hidden once the file itself is shown. */
const mediaLabel = /^(🎤 Nota de voz|🎵 Audio|📷 Foto|🎥 Video|📄 Documento(: .*)?|Sticker)$/u;

/** The photo, voice note (with its transcript), video or document of a WhatsApp message. */
function MessageMedia({ messageId, media }: { messageId: string; media: WhatsAppMedia }) {
  const src = `/api/v1/whatsapp/messages/${encodeURIComponent(messageId)}/media`;
  if (media.kind === "image" || media.kind === "sticker") {
    return <a className="whatsapp-media whatsapp-media--image" href={src} target="_blank" rel="noreferrer noopener" aria-label="Abrir foto">
      <img src={src} alt={media.kind === "sticker" ? "Sticker" : "Foto recibida por WhatsApp"} loading="lazy" />
    </a>;
  }
  if (media.kind === "audio") {
    return <div className="whatsapp-media whatsapp-media--audio">
      <audio controls preload="none" src={src} aria-label="Nota de voz" />
      <p className="whatsapp-media__transcript">
        <Mic size={14} aria-hidden="true" />
        {media.transcript_status === "done" && media.transcript
          ? <span>{media.transcript}</span>
          : media.transcript_status === "failed"
            ? <em>No se pudo transcribir; escúchala aquí.</em>
            : media.transcript_status === "skipped"
              ? <em>Nota de voz</em>
              : <em>Transcribiendo…</em>}
      </p>
    </div>;
  }
  if (media.kind === "video") {
    return <div className="whatsapp-media whatsapp-media--video"><video controls preload="metadata" src={src} aria-label="Video recibido por WhatsApp" /></div>;
  }
  const name = media.file_name || "Documento";
  return <a className="whatsapp-message-document" href={src} target="_blank" rel="noreferrer noopener" aria-label={`Abrir documento · ${name}`}>
    <FileText aria-hidden="true" /><span><strong>{name}</strong><small>{Math.max(1, Math.round(media.byte_size / 1024))} KB · Abrir</small></span><ExternalLink aria-hidden="true" />
  </a>;
}

export function WhatsAppMessageBody({ body, messageType, metadata, messageId, media }: { body: string; messageType?: string | undefined; metadata?: Record<string, unknown> | null | undefined; messageId?: string | undefined; media?: WhatsAppMedia | null | undefined }) {
  const blocks = media && messageId && mediaLabel.test(body.trim()) ? [] : parseBlocks(body);
  const asset = metadata?.attachment;
  const document = messageType === "document" && typeof asset === "string" && Object.hasOwn(schoolDocuments, asset) ? schoolDocuments[asset] : undefined;
  const ad = metadata?.ad && typeof metadata.ad === "object" ? metadata.ad as { title?: string; body?: string; source_url?: string; source_app?: string } : null;
  return <div className="whatsapp-message-body">
    {/* The Facebook/Instagram ad the customer tapped to write: no need to ask what they saw. */}
    {ad ? <div className="whatsapp-message-ad" aria-label="Anuncio de origen">
      <Megaphone aria-hidden="true" />
      <span>
        <small>Llegó de un anuncio{ad.source_app ? ` · ${/insta/i.test(ad.source_app) ? "Instagram" : /face|fb/i.test(ad.source_app) ? "Facebook" : ad.source_app}` : ""}</small>
        <strong>{ad.title || "Anuncio sin título"}</strong>
        {ad.body ? <em>{ad.body}</em> : null}
      </span>
      {ad.source_url && /^https?:\/\//.test(ad.source_url) ? <a href={ad.source_url} target="_blank" rel="noreferrer noopener">Ver anuncio <ExternalLink aria-hidden="true" /></a> : null}
    </div> : null}
    {media && messageId ? <MessageMedia messageId={messageId} media={media} /> : null}
    {document ? <a className="whatsapp-message-document" href={`/brochures/${document.file}`} target="_blank" rel="noreferrer noopener" aria-label={`Abrir PDF · ${document.title}`}>
      <FileText aria-hidden="true" /><span><strong>Folleto · {document.title}</strong><small>PDF adjunto</small></span><ExternalLink aria-hidden="true" />
    </a> : messageType === "document" && typeof metadata?.file_name === "string" ? (typeof metadata.document_id === "string" && /^[0-9a-f-]{36}$/i.test(metadata.document_id)
      ? <a className="whatsapp-message-document" href={`/api/v1/whatsapp/documents/${metadata.document_id}`} target="_blank" rel="noreferrer noopener" aria-label={`Abrir PDF · ${metadata.file_name}`}>
          <FileText aria-hidden="true" /><span><strong>{documentTitle(metadata)}</strong><small>{metadata.file_name} · PDF adjunto</small></span><ExternalLink aria-hidden="true" />
        </a>
      : <p className="whatsapp-message-document">
          <FileText aria-hidden="true" /><span><strong>{documentTitle(metadata)}</strong><small>{metadata.file_name} · PDF adjunto</small></span>
        </p>) : messageType === "document" ? <p className="whatsapp-message-document-unavailable">Documento adjunto · Vista previa no disponible</p> : null}
    {blocks.map((block, blockIndex) => {
    if (block.type === "list") {
      return <ul key={`list-${blockIndex}`}>{block.items.map((item, itemIndex) => (
        <li key={`item-${itemIndex}`}>{renderInline(item, `list-${blockIndex}-${itemIndex}`)}</li>
      ))}</ul>;
    }
    return <p key={`paragraph-${blockIndex}`}>{block.lines.map((line, lineIndex) => (
      <span key={`line-${lineIndex}`}>
        {lineIndex > 0 ? <br /> : null}
        {renderInline(line, `paragraph-${blockIndex}-${lineIndex}`)}
      </span>
    ))}</p>;
  })}</div>;
}

function parseBlocks(value: string): MessageBlock[] {
  const lines = value.replace(/\r\n?/g, "\n").split("\n");
  const blocks: MessageBlock[] = [];
  let paragraph: string[] = [];
  let items: string[] = [];
  const flushParagraph = () => {
    if (paragraph.length > 0) blocks.push({ type: "paragraph", lines: paragraph });
    paragraph = [];
  };
  const flushList = () => {
    if (items.length > 0) blocks.push({ type: "list", items });
    items = [];
  };

  for (const line of lines) {
    const bullet = /^\s*[-–•]\s+(.+)$/.exec(line);
    if (bullet?.[1]) {
      flushParagraph();
      items.push(bullet[1]);
      continue;
    }
    flushList();
    if (!line.trim()) {
      flushParagraph();
      continue;
    }
    paragraph.push(line);
  }
  flushList();
  flushParagraph();
  return blocks;
}

function renderInline(value: string, keyPrefix: string): ReactNode[] {
  const tokens = value.split(/(https?:\/\/[^\s<>]+|\*\*[^*\n]+\*\*|\*[^*\n]+\*)/g).filter(Boolean);
  return tokens.map((token, index) => {
    const key = `${keyPrefix}-${index}`;
    if (token.startsWith("**") && token.endsWith("**")) {
      return <strong key={key}>{token.slice(2, -2)}</strong>;
    }
    if (token.startsWith("*") && token.endsWith("*")) {
      return <strong key={key}>{token.slice(1, -1)}</strong>;
    }
    if (/^https?:\/\//.test(token)) {
      const { url, suffix } = splitUrlSuffix(token);
      return <span key={key}><a href={url} target="_blank" rel="noreferrer noopener">{url}</a>{suffix}</span>;
    }
    return token;
  });
}

function splitUrlSuffix(value: string): { url: string; suffix: string } {
  const suffix = /[.,;:!?)]*$/.exec(value)?.[0] ?? "";
  return { url: value.slice(0, value.length - suffix.length), suffix };
}
